import React from 'react';

import {CreateAnalysisParams} from 'analysis-tool/AnalysisDAO';
import {CreateAnalysisModal} from 'analysis-tool/create-analysis-modal/CreateAnalysisModal';
import {SeedDataConfigType} from 'analysis-tool/create-analysis-modal/SeedDataTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  isFeatureEnabledForProcedureType,
  ProceduresFeature,
} from 'procedures/utils/ProcedureCapabilityManagementHelpers';
import {StudySearchItem} from 'studies/StudySearchInput';
import {
  CREATE_ANALYSIS_NO_PERMISSION_MESSAGE,
  getInitialAnalysisNameForProcedureRun,
} from 'studies/typed/utils/analysis/analysisUtils';
import {createAnalysisForStudy} from 'studies/typed/utils/analysis/createAnalysisForStudy';
import ProcedureBasedStudyAnalysisModalBanner from 'studies/typed/utils/analysis/ProcedureBasedStudyAnalysisModalBanner';
import {getProcedureRunUIStringForStudyType} from 'studies/TypedStudyUtils';
import {type ActionItem} from 'ui-library/headers/Header';
import legacyRunModal from 'ui-library/legacyRunModal';

const STUDY_ARCHIVED_MESSAGE = 'Unarchive your study to generate an analysis';

export interface AnalyzeProcedureRunActionParams {
  procedureRunId: string;
  procedureRunName: string;
  isProcedureRunArchived: boolean;
  procedureType: ProcedureType;
  study: StudySearchItem;
  isStudyArchived: boolean;
  studyType: StudyType;
  actionOrigin: ActionOrigin;
}

/**
 * Builds the "Analyze" header action for a procedure run. Hook can be consumed by the procedure run header
 * action or the AnalyzeProcedureRunButton component.
 */
export function useAnalyzeProcedureRunAction({
  study,
  isStudyArchived,
  studyType,
  procedureRunId,
  procedureRunName,
  isProcedureRunArchived,
  procedureType,
  actionOrigin,
}: AnalyzeProcedureRunActionParams): ActionItem {
  const hasAnalyzePermission = isFeatureEnabledForProcedureType(
    procedureType,
    ProceduresFeature.CREATE_ANALYSIS
  );
  const isDisabled = !hasAnalyzePermission || isStudyArchived || isProcedureRunArchived;

  let disabledTooltip: string | undefined;
  if (isStudyArchived) {
    // Creating an analysis links it back to the study via a studyable association, which
    // triggers an auth error when the parent study is archived.
    disabledTooltip = STUDY_ARCHIVED_MESSAGE;
  } else if (isProcedureRunArchived) {
    disabledTooltip = `Unarchive your ${getProcedureRunUIStringForStudyType(studyType)} to generate an analysis`;
  } else if (!hasAnalyzePermission) {
    disabledTooltip = CREATE_ANALYSIS_NO_PERMISSION_MESSAGE;
  }

  const onSelect = async (): Promise<void> => {
    await legacyRunModal(CreateAnalysisModal, {
      actionOrigin,
      onCreateAnalysis: (data: CreateAnalysisParams) =>
        createAnalysisForStudy(data, {
          actionOrigin,
          studyType,
          procedureType,
          sourceObjectIds: [procedureRunId],
        }),
      initialStudySearchItems: [study],
      disableEditStudies: true,
      title: 'Generate analysis',
      initialAnalysisName: () => getInitialAnalysisNameForProcedureRun(study.name, procedureRunName),
      headerInlineBanner: <ProcedureBasedStudyAnalysisModalBanner studyType={studyType} />,
      hideDataSharingWarningBanner: true,
      initialSeedDataConfig: {
        type: SeedDataConfigType.PROCEDURE_RUN,
        data: {
          procedureRunIds: [procedureRunId],
        },
      },
    });
  };

  return {
    icon: `${ModelTypeConstants.ANALYSIS}` as const,
    name: 'Analyze',
    onSelect,
    isDisabled,
    disabledTooltip,
    dataTestAttrs: {component: 'AnalyzeProcedureRunButton', element: 'root'},
  };
}
